import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Player, PlayerEvent } from '@lottiefiles/react-lottie-player';
import cn from 'classnames';
import type { BBCommonProps } from 'types/story';
import useInView from 'common/components/useInView';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { LottieDefaultTrigger, LottieDirection, LottieEvEm, LottiePlaybackEvent, LottieTrigger } from './utils';
import css from './Lottie.scss';

const inViewConfig: Parameters<typeof useInView>[1] = {
	threshold: 0.5,
	callback: ([entry]) => {
		const isHigherThanViewport = entry.boundingClientRect.height > window.innerHeight;
		return isHigherThanViewport || entry.isIntersecting;
	},
};

enum Rewind {
	'start',
	'middle',
}

type Props = BBCommonProps;

const Lottie = (props: Props) => {
	const isPlayRequested = useRef(false);
	const playerRef = useRef<Player>(null);
	const [isReady, setIsReady] = useState(false);
	const inView = useInView(props.containerRef as RefObject<HTMLDivElement>, inViewConfig);
	const { editableModeProps: EMP, stateAttrs, uiConfig, isEditableMode, _id } = props;

	const otherProps = uiConfig.componentProps.other ?? {};
	const trigger = otherProps.ltTrigger ?? LottieDefaultTrigger;
	const src = otherProps.ltSrc;
	const hasSrc = src && src !== 'none';
	const keepLastFrame = otherProps.ltKeepLastFrame;
	const loop = isEditableMode ? false : otherProps.ltLoop ?? false;
	const direction = otherProps.ltDir === LottieDirection.backward ? (-1 as const) : (1 as const);
	const speed = parseFloat(otherProps.ltSpeed ?? '') || 1;
	const hover = false;

	const rewindTo = useCallback((position: Rewind) => {
		const player = playerRef.current;
		if (!player) return;
		const totalFrames = player.state.instance?.totalFrames ?? 0;
		const frame = { first: 0, middle: totalFrames / 2, last: totalFrames };
		const isBackward = player.props.direction === -1;
		player.setSeeker(position === Rewind.start ? (isBackward ? frame.last : frame.first) : frame.middle);
	}, []);

	/**
	 * Main function to start animation.
	 * Rewind to start position and play.
	 */
	const play = useCallback(() => {
		isPlayRequested.current = true;
		rewindTo(Rewind.start);
		playerRef.current?.play();
	}, [rewindTo]);

	// Set player config
	const setPlayerConfig = useCallback(() => {
		playerRef.current?.setLoop(loop);
		playerRef.current?.setPlayerDirection(direction);
		playerRef.current?.setPlayerSpeed(speed);
	}, [loop, direction, speed]);

	// Apply config change (from editor)
	useEffect(() => {
		if (isEditableMode) setPlayerConfig();
	}, [isEditableMode, setPlayerConfig]);

	// Listen custom events
	useEffect(() => {
		const listener = (event: LottiePlaybackEvent) => {
			const player = playerRef.current;
			if (!player) return;

			switch (event.data.action) {
				case 'previewAnimation':
					if (event.data.lottieId && event.data.lottieId === _id) play();
					break;
				case 'onAnswer': {
					const { isCorrect, answerNodeId } = event.data;
					const answer = document.getElementById(answerNodeId);
					const isSelected = () => answer && answer.contains(player.container);

					switch (trigger) {
						case LottieTrigger.onCardAnswer:
							play();
							break;
						case LottieTrigger.onCardAnswerCorrect:
							if (isCorrect) play();
							break;
						case LottieTrigger.onCardAnswerIncorrect:
							if (!isCorrect) play();
							break;
						case LottieTrigger.onAnswer:
							if (isSelected()) play();
							break;
						case LottieTrigger.onAnswerCorrect:
							if (isSelected() && isCorrect) play();
							break;
						case LottieTrigger.onAnswerIncorrect:
							if (isSelected() && !isCorrect) play();
							break;
						default:
							break;
					}
					break;
				}
				case 'onMultipleAnswerSubmit': {
					const isSelected =
						trigger === LottieTrigger.onAnswer &&
						event.data.answerNodeId.some(answerId =>
							document.getElementById(answerId)?.contains(player.container)
						);

					if (trigger === LottieTrigger.onCardAnswer || isSelected) play();
					break;
				}
				case 'onFormSubmit':
					if (trigger === LottieTrigger.onFormSubmit) play();
					break;
				default:
					break;
			}
		};
		LottieEvEm.addListener('playback', listener);
		return () => {
			LottieEvEm.removeListener('playback', listener);
		};
	}, [_id, play, trigger]);

	// Default start play
	useEffect(() => {
		if (!isEditableMode && isReady && trigger === LottieTrigger.load && inView) {
			play();
		}
	}, [inView, trigger, isReady, play, isEditableMode]);

	// DOM node event listeners
	const eventListeners = () => {
		const listeners = {} as Exclude<BBCommonProps['eventListeners'], undefined>;

		if (!isEditableMode) {
			if (trigger === LottieTrigger.onClick) {
				listeners.onClick = event => {
					props.eventListeners?.onClick?.(event);
					play();
				};
			} else if (trigger === LottieTrigger.onHover) {
				listeners.onMouseEnter = event => {
					props.eventListeners?.onMouseEnter?.(event);
					play();
				};
			}
		}

		return { ...props.eventListeners, ...listeners };
	};

	return (
		<div
			{...uiConfig.nodeProps}
			{...stateAttrs}
			{...eventListeners()}
			{...EMP?.nodeProps}
			style={uiConfig.nodeProps?.style}
			className={cn(css.lottie, uiConfig.nodeProps.className, EMP?.nodeProps?.className)}
			ref={props.containerRef as RefObject<HTMLDivElement>}
		>
			{hasSrc ? (
				<Player
					key={`${src}-${_id}`}
					id={_id}
					ref={playerRef}
					src={src}
					background="transparent"
					controls={false}
					direction={direction}
					loop={loop}
					hover={hover}
					autoplay={false}
					keepLastFrame={keepLastFrame}
					speed={speed}
					onEvent={event => {
						if (event === PlayerEvent.InstanceSaved) {
							setPlayerConfig();
							rewindTo(isEditableMode ? Rewind.middle : Rewind.start);
							setIsReady(true);

							/**
							 * This ensures that if a play request was initiated before the player was ready,
							 * the play action will still be executed once the player becomes ready.
							 *
							 * For instance if in `correct` state having another `src`, then when user answers correctly
							 * play is requested immediately, but lottie animation file assigned for the correct state
							 * will be loaded a bit later.
							 */
							if (isPlayRequested.current) {
								play();
							}
						}
					}}
				/>
			) : (
				<div className={css.empty} data-text="No file" />
			)}
		</div>
	);
};

/*
	const [isLottieLoaded, setIsLottieLoaded] = useState('lottie' in window);

	useEffect(() => {
		if ('lottie' in window) {
			setIsLottieLoaded(true);
		} else {
			const script = document.createElement('script');
			script.onload = () => setIsLottieLoaded(true);
			script.src = 'https://unpkg.com/@lottiefiles/lottie-player@latest/dist/lottie-player.js';
			document.body.appendChild(script);
		}
	}, []);


	return ({isLottieLoaded ? (
		<lottie-player src={src} background="transparent" speed="1" loop controls autoplay />
	) : null});
 */

export default withCardTransitionContext(Lottie);
